import React, { forwardRef, useMemo } from 'react';

import { useGetHandlers } from '@vlabs/api/luna-api/client';
import { OptionsFilter } from '@vlabs/monokit/shared/ui/options-filter';
import type { Option } from '@vlabs/monokit/shared/ui/options-filter';
import { PopoverWithButton } from '@vlabs/monokit/shared/ui/popovers';
import type { PopoverWithButtonProps } from '@vlabs/monokit/shared/ui/popovers';
import { validate } from '@vlabs/shared';

import { useWorkspaceFilters } from '../../useWorkspaceFilters';

function HandlersFilterPopover({
  ...props
}: PopoverWithButtonProps, ref: React.ForwardedRef<HTMLDivElement>): React.JSX.Element {
  const { filters, updateFilters } = useWorkspaceFilters();
  const getHandlers = useGetHandlers({ page_size: 1000 });

  const handlerOptions = useMemo<Option[]>(() => getHandlers.data?.map((handler) => ({
    label: handler.description,
    value: handler.handler_id,
  })) ?? [], [getHandlers.data]);
  const selectedHandlers = filters.handler_ids ?? [];

  const selectedHandlerIds = selectedHandlers.map(({ value }) => value);

  const handleChange = (nextHandlerIds: string[]) => {
    const nextHandlers = nextHandlerIds.map((handlerId) => (
      handlerOptions.find(({ value }) => value === handlerId) ?? {
        label: handlerId,
        value: handlerId,
      }
    ));

    updateFilters({
      handler_ids: nextHandlers.length > 0 ? nextHandlers : undefined,
    });
  };

  return (
    <PopoverWithButton
      {...props}
      isButtonDisabled={selectedHandlers.length === 0}
      onClickReset={() => updateFilters({ handler_ids: undefined })}
      ref={ref}
    >
      <OptionsFilter
        isCreatable
        name="handler_ids"
        onChange={handleChange}
        options={handlerOptions}
        validateProp={validate.UUIDValidator()}
        value={selectedHandlerIds}
        visibleCount={5}
      />
    </PopoverWithButton>
  );
}

const HandlersFilterPopoverWithRef = forwardRef(HandlersFilterPopover);
export { HandlersFilterPopoverWithRef as HandlersFilterPopover };
